import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

document.querySelectorAll<HTMLElement>("[data-gsap]").forEach(el => {
    const animationType = el.dataset.gsap;
    const start = el.dataset.gsapStart || "top bottom";
    const end = el.dataset.gsapEnd || "top top";
    const trigger = el.dataset.gsapTrigger || el;
    const duration = el.dataset.gsapDuration || 0.5;
    const scrub = el.dataset.gsapScrub != null;
    const offsetYTo = el.dataset.gsapOffsetYTo || 0;

    if (animationType === "fade-in") {
        const fromProperties: Record<string, any> = {
            opacity: 0
        };
        const toProperties = {
            opacity: 1
        };

        if (offsetYTo) {
            // Check if the value is a percentage or pixel value
            const isPercent = typeof offsetYTo === 'string' && offsetYTo.includes('%');
            const propertyName = isPercent ? 'yPercent' : 'y';
            const value = isPercent ? parseFloat(offsetYTo) : offsetYTo;

            fromProperties[propertyName] = 0;
            toProperties[propertyName] = value;
        }

        gsap.fromTo(el,
            {
                ...fromProperties
            },
            {
                ...toProperties,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: trigger,
                    start: start,
                    end: end,
                    scrub: scrub,
                    toggleActions: "play none none reverse", // play when enters, reverse when leaves
                },
                duration: duration, // Optional: Add a duration for the animation
                immediateRender: false // important!
        });
    }

    if (animationType === "fade-out") {
        const fromProperties: Record<string, any> = {
            opacity: 1
        };
        const toProperties = {
            opacity: 0
        };

        if (offsetYTo) {
            // Check if the value is a percentage or pixel value
            const isPercent = typeof offsetYTo === 'string' && offsetYTo.includes('%');
            const propertyName = isPercent ? 'yPercent' : 'y';
            const value = isPercent ? parseFloat(offsetYTo) : offsetYTo;

            fromProperties[propertyName] = 0;
            toProperties[propertyName] = value;
        }

        gsap.fromTo(el,
            {
                ...fromProperties
            },
            {
                ...toProperties,
                scrollTrigger: {
                    trigger: trigger,
                    start: start,
                    end: end,
                    scrub: scrub,
                    toggleActions: "play none none reverse", // play when enters, reverse when leaves
                },
                duration: duration, // Optional: Add a duration for the animation
                immediateRender: false // important!
        });
    }

    if (animationType === "fade-in-out") {
        // Create a timeline for fade in and fade out
        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: trigger,
                start: start,
                end: end,
                scrub: scrub,
            }
        });

        // Fade in from 0 to 1 in the first half
        timeline.fromTo(el,
            { opacity: 0 },
            { opacity: 1, duration: 0.5, ease: "power2.out" }
        );

        // Fade out from 1 to 0 in the second half
        timeline.to(el,
            { opacity: 0, duration: 0.5, ease: "power2.in" }
        );
    }
  
    if (animationType === "slide-in-left" || animationType === "slide-in-right") {
        // Distance is a percentage of the element's own width (xPercent).
        const distance = Math.abs(parseFloat(String(el.dataset.gsapDistance || 100)));
        const fromXPercent = animationType === "slide-in-left" ? -distance : distance;
        // Tie progress to scroll position. Start once the element enters,
        // finish when it is ~50% visible (its center reaches viewport center).
        const slideStart = el.dataset.gsapStart || "top 90%";
        const slideEnd = el.dataset.gsapEnd || "center 65%";
        const slideScrub = el.dataset.gsapScrub != null ? scrub : true;

        // Desktop only: the comparison image wrappers are hidden below 1023px,
        // so there is no slide animation on mobile. matchMedia reverts cleanly.
        gsap.matchMedia().add("(min-width: 1023px)", () => {
            gsap.fromTo(el,
                {
                    opacity: 0,
                    xPercent: fromXPercent
                },
                {
                    opacity: 1,
                    xPercent: 0,
                    ease: "none",
                    scrollTrigger: {
                        trigger: trigger,
                        start: slideStart,
                        end: slideEnd,
                        scrub: slideScrub,
                    },
                    immediateRender: false
                });
        });
    }

    if (animationType === "reveal-up") {
        // Rises from an offset to its natural position (y:0) while fading in.
        // Only y/opacity are tweened so any CSS transform (e.g. rotate) is preserved.
        const offset = parseFloat(String(el.dataset.gsapOffsetY || 60));
        const revealStart = el.dataset.gsapStart || "top 75%";

        gsap.fromTo(el,
            {
                opacity: 0,
                y: offset
            },
            {
                opacity: 1,
                y: 0,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: trigger,
                    start: revealStart,
                    end: end,
                    scrub: scrub,
                    toggleActions: "play none none reverse",
                },
                duration: duration,
                // Apply the hidden from-state on load so it doesn't flash the
                // element in its natural position before the reveal triggers.
                immediateRender: true
            });
    }

    if (animationType === "fly-in") {
        // Flies in from an x/y offset (px) while fading in, with a slight
        // overshoot for a playful "drop into place" feel. Only x/y/opacity are
        // tweened so a CSS float/transform on a child element is unaffected.
        const flyX = parseFloat(String(el.dataset.gsapFlyX || 0));
        const flyY = parseFloat(String(el.dataset.gsapFlyY || 60));
        const flyStart = el.dataset.gsapStart || "top 85%";
        const flyDuration = parseFloat(String(el.dataset.gsapDuration || 0.8));

        gsap.fromTo(el,
            {
                opacity: 0,
                x: flyX,
                y: flyY
            },
            {
                opacity: 1,
                x: 0,
                y: 0,
                ease: "back.out(1.4)",
                scrollTrigger: {
                    trigger: trigger,
                    start: flyStart,
                    end: end,
                    scrub: scrub,
                    toggleActions: "play none none reverse",
                },
                duration: flyDuration,
                // Apply the hidden from-state on load so it doesn't flash in its
                // natural position before the fly-in triggers.
                immediateRender: true
            });
    }

    if (animationType === "color-transition") {
        const colorFrom = el.dataset.gsapColorFrom || "red";
        const colorTo = el.dataset.gsapColorTo || "blue";
        const cssVar = el.dataset.gsapCssVar;
        console.log(trigger)

        if (cssVar) {
            gsap.fromTo(el,
                {
                    [cssVar]: colorFrom
                },
                {
                    [cssVar]: colorTo,
                    ease: "power2.in",
                    scrollTrigger: {
                        trigger: '.qr-intro',
                        start: 'bottom 80%',
                        end: 'bottom 15%',
                        scrub: scrub,
                        toggleActions: "play none none reverse",
                    },
                    duration: duration,
                    immediateRender: false
            });
        }
    }

    // You can extend this to support more animation types
});

window.addEventListener('load', () => {
ScrollTrigger.refresh(); // recalculate trigger positions after everything is loaded
});